import { create } from 'zustand';
import type { Task } from '../lib/api';
import { tasksApi } from '../lib/api';
import { getSocket } from '../lib/socket';

type BoardFilter = 'all' | 'mine';

interface TasksState {
  tasks: Task[];
  filter: BoardFilter;
  loading: boolean;

  setFilter: (f: BoardFilter) => void;
  loadTasks: () => Promise<void>;
  createTasks: (body: { title: string; assignedTo: string[]; threadId?: string; dueAt?: string; description?: string }) => Promise<Task[]>;
  updateTask: (id: string, patch: { status?: string; title?: string; dueAt?: string | null; description?: string | null }) => Promise<void>;
  deleteTask: (id: string) => Promise<void>;
  setupSocketListeners: () => void;
}

export const useTasksStore = create<TasksState>((set, get) => ({
  tasks: [],
  filter: 'mine',
  loading: false,

  setFilter: (f) => set({ filter: f }),

  loadTasks: async () => {
    set({ loading: true });
    try {
      const { data } = await tasksApi.list();
      set({ tasks: data });
    } finally {
      set({ loading: false });
    }
  },

  createTasks: async (body) => {
    const { data } = await tasksApi.create(body);
    set((s) => {
      const ids = new Set(data.map((t) => t.id));
      const existing = s.tasks.filter((t) => !ids.has(t.id));
      return { tasks: [...data, ...existing] };
    });
    return data;
  },

  updateTask: async (id, patch) => {
    try {
      const { data: updated } = await tasksApi.update(id, patch);
      set((s) => ({
        tasks: s.tasks.map((t) => (t.id === id ? updated : t)),
      }));
    } catch {
      get().loadTasks();
    }
  },

  deleteTask: async (id) => {
    await tasksApi.delete(id);
    set((s) => ({ tasks: s.tasks.filter((t) => t.id !== id) }));
  },

  setupSocketListeners: () => {
    const socket = getSocket();

    socket.on('task:created', (task: Task) => {
      set((s) => {
        if (s.tasks.find((t) => t.id === task.id)) return s;
        return { tasks: [task, ...s.tasks] };
      });
    });

    socket.on('task:updated', (task: Task & { deleted?: boolean }) => {
      if (task.deleted) {
        set((s) => ({ tasks: s.tasks.filter((t) => t.id !== task.id) }));
      } else {
        set((s) => ({
          tasks: s.tasks.map((t) => (t.id === task.id ? task : t)),
        }));
      }
    });
  },
}));
